import * as React from 'react';
import { cn } from '../../../utils';
import { Add, ThreeDots } from '../../Icon/icon';
import { Arrow, Icon } from 'rcd-cpp-ui/ui/icon';
import { Dropdown } from '../../Dropdown/dropdown';
import { Metadata } from '../../../types/Metadata';
import URLManager from '../../../utils/urlManager';
import { useSelector } from '../../../utils/useSelector';
import { ContextMenuLink } from '../../ContextMenuLink/contextMenuLink';

import './navGroup.scss';

type Option = { name: string; Icon: any };

export interface NavGroupProps<M extends Metadata = Metadata> {
  name: string;
  options?: Option[];
  handleSelectOption?: (o: Option, item?: M) => void;
  items: M[];
  getItemIcon: (item: M) => any;
  selectedItemId?: string;
  handleSelectItem: (item: M) => void;
  getItemOptions?: (item: M) => Option[];
  onAdd?: () => void;
  disabled?: boolean;
}

export const NavGroup = <M extends Metadata>({
  name,
  disabled,
  options,
  onAdd,
  handleSelectOption,
  items,
  handleSelectItem,
  selectedItemId,
  getItemOptions,
  getItemIcon
}: NavGroupProps<M>) => {
  const [showMenuForId, setShowMenuForId] = React.useState<string>(null);
  const [isExpanded, setExpanded] = React.useState(null);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  React.useEffect(() => {
    if (isExpanded === null && selectedItemId && items?.length) {
      const item = items.filter(i => i.id == selectedItemId)[0];
      if (item) setExpanded(true);
    }
  }, [items, selectedItemId]);
  return (
    <div className={cn({ 'nav-group-collasped': !isExpanded }, 'nav-group')}>
      <NavGroupHeader
        name={name}
        isExpanded={isExpanded}
        toggleIsExpanded={() => {
          setExpanded(!isExpanded);
        }}
      >
        {!disabled && (
          <span>
            {!!onAdd && (
              <Icon onClick={onAdd}>
                <Add />
              </Icon>
            )}
            {!!options?.length && (
              <Dropdown
                options={options}
                onSelect={handleSelectOption}
                renderOption={(o: Option) => {
                  const I = o.Icon;
                  return (
                    <div>
                      <span>{I && <I />}</span>
                      <span className="dd-text" data-txt={o.name}>
                        {o.name}
                      </span>
                    </div>
                  );
                }}
              >
                <ThreeDots />
              </Dropdown>
            )}
          </span>
        )}
      </NavGroupHeader>
      <div className={cn({ 'cp-nav-collapsible': !items?.length })}>
        {items?.map(v => {
          const itemOptions = getItemOptions ? getItemOptions(v) : null;
          return (
            <div
              key={v.id}
              className={cn(
                {
                  'selected-ngi': v.id == selectedItemId,
                  'show-ngi-menu': v.id == showMenuForId,
                  'has-ngi-menu': !!itemOptions?.length
                },
                'nav-group-item'
              )}
            >
              <ContextMenuLink
                href={URLManager.generateUrl(selectedWorkspace, {
                  v: v?.props?.refId
                })}
                onNavigate={() => handleSelectItem(v)}
              >
                <span>{getItemIcon(v)}</span>
                <span
                  className="cp-nav-collapsible view-name"
                  title={v?.props?.name}
                >
                  {v.props.name}
                </span>
                <span className="cp-nav-collapsible">
                  {v.id == selectedItemId && (
                    <Arrow direction="right" type="solid" />
                  )}
                </span>
              </ContextMenuLink>
              {!disabled && !!itemOptions?.length && (
                <div className="nav-group-item-menu">
                  <Dropdown
                    position="right"
                    options={itemOptions}
                    renderOption={(o: Option) => {
                      const I = o.Icon;
                      return (
                        <div>
                          <span>{I && <I />}</span>
                          <span className="dd-text" data-txt={o.name}>
                            {o.name}
                          </span>
                        </div>
                      );
                    }}
                    onSelect={o => handleSelectOption(o, v)}
                    onStateChanged={e => {
                      setShowMenuForId(e ? v.id : null);
                    }}
                  >
                    <ThreeDots />
                  </Dropdown>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const NavGroupHeader = ({
  name,
  isExpanded,
  toggleIsExpanded,
  children
}: Pick<NavGroupProps, 'name'> & {
  children?: any;
  isExpanded: boolean;
  toggleIsExpanded: () => void;
}) => (
  <div>
    <span onClick={() => toggleIsExpanded()}>
      <Arrow direction={isExpanded ? 'down' : 'right'} type="solid" />
    </span>
    <span onClick={() => toggleIsExpanded()}>{name}</span>
    {children || <span></span>}
  </div>
);
